import { Component, inject, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpSharedServiceService } from 'src/app/utility/services/shared-http/http-shared-service.service';
import { CommonTaskService } from 'src/app/utility/services/common-task/common-task.service';
import { FormsModule } from '@angular/forms';
import { DatePipe, DecimalPipe } from '@angular/common';
import { UserService } from 'src/app/utility/services/user/user.service';
import { USER_TOKEN } from 'src/app/utility/authentication/user-token.util';
import { MaterialModule } from 'src/app/common/material-module';
import { PartLookupComponent } from 'src/app/common/part-lookup/part-lookup.component';
import { Role } from 'src/app/common/interfaces/token/role';
import { RoleEnum } from 'src/app/common/interfaces/token/role-enum';
import { ConfirmQuoteDialogComponent } from './dialog/confirm-quote-dialog/confirm-quote-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { SendVerificationDialogComponent } from './dialog/send-verification-dialog/send-verification-dialog.component';
import { ConfirmEmailDialogComponent } from './dialog/confirm-email-dialog/confirm-email-dialog.component';
import { NotificationService } from 'src/app/utility/services/notification/notification.service';
import { ContactService } from 'src/app/utility/services/contact/contact.service';
import { NetsuiteService } from 'src/app/utility/services/netsuite/netsuite.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

interface QuoteData {
  quoteID: string,
  buid: string,
  // isactive: boolean,
  // isinstant: boolean,
  datecreated: string,
  // itemcount: number,
  // items: any,
  status: string,
  ecastatus: string,
  ecastatusint: string,
  origin: string,
  // Backend selling price of the quote/order, shown verbatim. Null when unpriced.
  amount: number | null,
}

// The facilities a contact can be routed to, keyed by the cfg_id PUT
// admin/contact takes. US contacts are stored with bu_id 1 and no CfgID; the
// European facilities all sit under bu_id 7. Only European contacts can change
// facility, and only between the European ones — US is shown but not offered.
const US_CFG_ID = 1;
const US_BU_ID = 1;
const EUROPE_BU_ID = 7;
const EUROPE_FACILITIES: { cfgId: number, label: string }[] = [
  { cfgId: 4, label: 'UK' },
  { cfgId: 5, label: 'IT' },
  { cfgId: 6, label: 'FR' },
];

// Default currencies a European (bu_id 7) contact can be given.
const EUROPE_CURRENCIES = ['EUR', 'GBP'];

// ECA is a US export-control check, so European (bu_id 7) contacts don't see
// the ECA Status and Action columns on their quotes and orders.
const ACTIVITY_COLUMNS = ['ecastatus', 'quoteID', 'origin', 'datecreated', 'status', 'amount', 'action'];
const EUROPE_ACTIVITY_COLUMNS = ['quoteID', 'origin', 'datecreated', 'status', 'amount'];

@Component({
    selector: 'app-mono-eca',
    templateUrl: './contact.component.html',
    styleUrls: ['./contact.component.scss'],
    imports: [FormsModule, MaterialModule, DatePipe, DecimalPipe, PartLookupComponent, MatChipsModule]
})
export class ContactComponent implements AfterViewInit {
  router = inject(Router);
  private notification = inject(NotificationService);
  private _http = inject(HttpSharedServiceService);
  private commonTask = inject(CommonTaskService);
  private readonly token = inject(USER_TOKEN)
  private dialog = inject(MatDialog);
  userService = inject(UserService);
  private contactService = inject(ContactService);
  private netsuiteService = inject(NetsuiteService);
  // Quotes and orders share one column set.
  get activityColumns(): string[] {
    return Number(this.formData.bu_id) === EUROPE_BU_ID ? EUROPE_ACTIVITY_COLUMNS : ACTIVITY_COLUMNS;
  }

  @ViewChild('quotePaginator') quotePaginator!: MatPaginator;
  @ViewChild('orderPaginator') orderPaginator!: MatPaginator;

  roles = RoleEnum;


  constructor() {
    this.contactID = "";
  }

  formData = {
    hs_full_name: " ",
    hs_first_name: "",
    hs_last_name: "",
    bu_id: "",
    profileID: "",
    contactID: "",
    dealid: "",
    dealname: "",
    dealowner: "",
    dealstage: "",
    creationdate: "",
    updateddate: "",
    ecastatus: "",
    hs_country: "",
    hs_email: "",
    hs_phone: "",
    hs_account_name: "",
    admin_email: "",
    industry_name: " ",
    admin_registration_date: " ",
    admin_last_quote_date: " ",
    admin_full_name: " ",
    admin_first_name: " ",
    admin_last_name: " ",
    admin_phone: " ",
    admin_phone_ext: " ",
    admin_fax: " ",
    admin_mobile: " ",
    admin_company_name: " ",
    admin_account_name: " ",
    admin_sales_tier: " ",
    admin_is_active: " ",
  }

  quoteDataSource = new MatTableDataSource<QuoteData>([]);
  orderDataSource = new MatTableDataSource<QuoteData>([]);
  quoterows: QuoteData[] = [];
  orderrows: QuoteData[] = [];
  emailTarget: string = "";
  quoteTarget: string = "";
  contactID: string | null;

  // The Quickparts column's editable fields share one Undo/Save and go out in
  // a single PUT admin/contact. Name, phone and facility live on the user
  // record and company name on the profile, but the endpoint writes both.
  firstNameDraft: string = "";
  lastNameDraft: string = "";
  phoneDraft: string = "";
  companyNameDraft: string = "";
  facilityDraft: number | null = null;
  currencyDraft: string | null = null;
  savingQuickparts: boolean = false;
  firstNameMaxLength: number = 50; // @FName on the UM_UpdateUserByUserID sproc
  lastNameMaxLength: number = 100; // @LName on the UM_UpdateUserByUserID sproc
  phoneMaxLength: number = 50; // @UserPhone on the UM_UpdateUserByUserID sproc
  companyNameMaxLength: number = 50; // enforced by the UM_UpdateProfile sproc
  // language_id is required by PUT admin/contact but only comes back nested
  // under profile on the GET, so it is captured with the rest of the details
  languageId: number | null = null;

  // Facility the contact is routed to, as a cfg_id. Null when the stored
  // bu_id/cfg_id pair is not a known facility.
  readonly facilities = EUROPE_FACILITIES;
  facilityCfgId: number | null = null;

  readonly currencies = EUROPE_CURRENCIES;
  defaultCurrency: string = "";

  // NetSuite account id for this profile. Read via
  // netsuite/get-account-from-profile, written via netsuite/associate-account.
  netsuiteAccountId: string = "";
  netsuiteAccountIdDraft: string = "";
  savingNetsuiteAccountId: boolean = false;
  netsuiteAccountIdMaxLength: number = 20;

  modals: string[] = [
    "ecamodal",
    "newquotemodal",
    "newprofilemodal",
  ];


  // formData defaults are single spaces, so blanks need trimming before display
  display(value: any): string {
    const text = (value ?? '').toString().trim();
    return text === '' ? '—' : text;
  }

  ngAfterViewInit() {
    // Connect paginators after view initialization
    this.quoteDataSource.paginator = this.quotePaginator;
    this.orderDataSource.paginator = this.orderPaginator;
  }

  ngOnInit() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    this.contactID = "";

    let windowurl = window.location.href;
    if (windowurl.includes('Contact')) {
      let contactid = windowurl.substring(windowurl.lastIndexOf('/') + 1)
      console.log(`Got a contact from the url: ${contactid}`);
      localStorage.setItem('requestedpage', 'Contact');
      localStorage.setItem('pageid', contactid);
      this.contactService.currentContactId.set(contactid);
      this.contactID = contactid;
      this.enableQuoteButton();
    } else if (localStorage.getItem('requestedpage') == 'Contact') {
      this.contactID = localStorage.getItem('pageid');
      this.enableQuoteButton();
    }


    if (!this.token) {
      this.userService.loginRedirect();
    } else if (this.contactID !== null && this.contactID != "") {
      this.formData.contactID = this.contactID;
      this.contactService.getProfileFromContactId(this.contactID).subscribe({
        next: (responseMSprofile: any) => {
          if (responseMSprofile == null) { console.log('null response'); return; }
          console.log(responseMSprofile);
          var doSearch = false;

          this.formData.hs_email = responseMSprofile.hs_email ?? "";
          this.formData.profileID = responseMSprofile.profile_id;

          if (responseMSprofile.profile_id == null) {
            console.log('no profile ID (unhappy path)');
            if (this.formData.hs_email != "") {
              console.log("Email present, searching by that...")
              this.getProfileByEmail();
            } else {
              console.error(`Unable to continue, no email address found`);
            }
          } else {
            console.log(`Got profile ID from microservice: ${responseMSprofile.profile_id}`);
            doSearch = true;
            this.getAdminDetails();
            this.getHubspotContactDetails();
            this.getNetsuiteAccount();
            this.enableQuoteButton();
          }
          this.formData.hs_full_name = responseMSprofile.hs_first_name + " " + responseMSprofile.hs_last_name;
          this.formData.hs_first_name = responseMSprofile.hs_first_name;
          this.formData.hs_last_name = responseMSprofile.hs_last_name;
          this.formData.hs_country = responseMSprofile.country_code;
          this.formData.admin_phone = responseMSprofile.phone;
          this.formData.admin_phone_ext = responseMSprofile.phone_ext;

          this.commonTask.addRecentEntry('contact', this.contactID!, this.formData.hs_email);

          // REACTIVATE ME AFTER TESTING
          if (doSearch) { this.getQuoteList(this.formData.profileID); }
        }
      });
    }

    // Generate DF Token and sales User Token
    this.commonTask.generateSecreteHash(this._http)

    // Activate create quote button if a profileID is present
    if (this.formData.profileID != '' && this.formData.profileID != null) {
      this.enableQuoteButton();
    }

  }

  newQuotemodalShow() {
    const dialogRef = this.dialog.open(ConfirmQuoteDialogComponent, {
      data: { hs_full_name: this.formData.hs_full_name }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.postNewQuote();
      }
    });
  }

  getAdminDetails() {
    console.log(`Getting profile details for ${this.formData.profileID} from ITO api`);
    // let params = [ { key: "profile_id", value: this.formData.profileID } ]
    this.contactService.getContactByProfileId(this.formData.profileID).subscribe({
      next: (responseAdminProfile: any) => {
        console.log(responseAdminProfile);

        // Hubspot data can occasionally change (account merges, etc), but quote owner for ECA is handled by us (admin)
        this.formData.admin_email = responseAdminProfile.email;

        // Other data
        // this.formData.industry_name = responseAdminProfile.profile.industry;
        this.formData.industry_name = "Industry Name";
        this.formData.admin_company_name = responseAdminProfile.company_name;
        this.formData.admin_full_name = responseAdminProfile.first_name + ' ' + responseAdminProfile.last_name;
        this.formData.bu_id = responseAdminProfile.bu_id;

        Object.keys(this.formData).forEach(fd_key => {
          let oldkey = fd_key as keyof typeof this.formData;
          let newkey = fd_key.replace(/^admin_/, '');
          if (newkey in responseAdminProfile) {
            let reskey = responseAdminProfile[newkey];
            this.formData[oldkey] = (reskey != '') ? reskey : '';
          }
        });

        // The admin API's `phone`/`phone_ext` are dead properties — the Contact
        // model maps them from Phone/PhoneExt columns that CM_GetContactInfoByProfileID
        // never returns, so they are always "". The stored number is `user_phone`
        // ([User].UserPhone), which is also what PUT admin/contact writes, and the
        // same column Phoenix reads/writes as `phone` on PUT User.
        // Note `user_ext` ([User].UserExt) is not a dial extension — Phoenix puts the
        // country dial code there (e.g. "+1") and prefixes it to the number on display.
        this.formData.admin_phone = responseAdminProfile?.user_phone ?? "";
        this.formData.admin_phone_ext = responseAdminProfile?.user_ext ?? "";

        this.languageId = responseAdminProfile?.profile?.language_id ?? null;
        this.facilityCfgId = this.facilityFromContact(responseAdminProfile);
        this.defaultCurrency = this.currencyFromContact(responseAdminProfile);
        this.resetQuickparts();
      }
    })
  }

  get quickpartsChanged(): boolean {
    return this.nameChanged || this.phoneChanged || this.companyNameChanged || this.facilityChanged || this.currencyChanged;
  }

  // The first problem that would stop a save, or null when the drafts are valid.
  get quickpartsError(): string | null {
    const first_name = this.firstNameDraft.trim();
    const last_name = this.lastNameDraft.trim();
    const company_name = this.companyNameDraft.trim();
    if (first_name == '' || last_name == '') { return 'First and last name are required'; }
    if (first_name.length > this.firstNameMaxLength) { return `First name must be ${this.firstNameMaxLength} characters or fewer`; }
    if (last_name.length > this.lastNameMaxLength) { return `Last name must be ${this.lastNameMaxLength} characters or fewer`; }
    if (this.phoneDraft.trim().length > this.phoneMaxLength) { return `Phone must be ${this.phoneMaxLength} characters or fewer`; }
    if (company_name == '') { return 'Company name is required'; }
    if (company_name.length > this.companyNameMaxLength) { return `Company name must be ${this.companyNameMaxLength} characters or fewer`; }
    if (this.facilityChanged && this.facilityDraft == null) { return 'Select a facility'; }
    if (this.currencyChanged && this.currencyDraft == null) { return 'Select a currency'; }
    return null;
  }

  get canSaveQuickparts(): boolean {
    return this.quickpartsChanged
      && !this.savingQuickparts
      && this.quickpartsError == null
      && this.languageId != null
      && this.formData.profileID != '';
  }

  resetQuickparts() {
    this.firstNameDraft = this.savedFirstName();
    this.lastNameDraft = this.savedLastName();
    this.phoneDraft = this.savedPhone();
    this.companyNameDraft = this.savedCompanyName();
    this.facilityDraft = this.facilityCfgId;
    this.currencyDraft = this.currencies.includes(this.defaultCurrency) ? this.defaultCurrency : null;
  }

  saveQuickparts() {
    if (this.formData.profileID == '' || this.formData.profileID == null) {
      this.notification.error('No QP profile to update');
      return;
    }
    const error = this.quickpartsError;
    if (error != null) {
      this.notification.error(error);
      return;
    }
    if (this.languageId == null) {
      this.notification.error('Could not update contact — no language on this profile');
      return;
    }

    // company_name and language_id are required on every call. The optional
    // fields are only sent when they changed, so untouched values are left alone.
    const payload: Parameters<ContactService['updateContact']>[0] = {
      profile_id: Number(this.formData.profileID),
      company_name: this.companyNameDraft.trim(),
      language_id: this.languageId,
    };
    if (this.nameChanged) {
      payload.first_name = this.firstNameDraft.trim();
      payload.last_name = this.lastNameDraft.trim();
    }
    if (this.phoneChanged) { payload.phone = this.phoneDraft.trim(); }
    if (this.facilityChanged && this.facilityDraft != null) { payload.cfg_id = this.facilityDraft; }
    if (this.currencyChanged && this.currencyDraft != null) { payload.default_currency = this.currencyDraft; }

    this.savingQuickparts = true;
    this.contactService.updateContact(payload).subscribe({
      next: (updatedContact: any) => {
        this.formData.admin_first_name = updatedContact?.first_name ?? payload.first_name ?? this.formData.admin_first_name;
        this.formData.admin_last_name = updatedContact?.last_name ?? payload.last_name ?? this.formData.admin_last_name;
        this.formData.admin_full_name = this.formData.admin_first_name + ' ' + this.formData.admin_last_name;
        this.formData.admin_phone = updatedContact?.user_phone ?? payload.phone ?? this.formData.admin_phone;
        this.formData.admin_company_name = updatedContact?.company_name ?? payload.company_name;
        if (updatedContact) {
          this.formData.bu_id = updatedContact.bu_id ?? this.formData.bu_id;
          this.facilityCfgId = this.facilityFromContact(updatedContact);
          this.defaultCurrency = this.currencyFromContact(updatedContact);
        } else if (payload.cfg_id != null) {
          this.facilityCfgId = payload.cfg_id;
        }
        this.resetQuickparts();
        this.savingQuickparts = false;
        this.notification.success('Contact updated');
      },
      error: (err: any) => {
        this.savingQuickparts = false;
        console.error('update contact failed', err);
        this.notification.error('Could not update contact');
      }
    })
  }

  private get nameChanged(): boolean {
    return this.firstNameDraft.trim() !== this.savedFirstName()
      || this.lastNameDraft.trim() !== this.savedLastName();
  }

  private get phoneChanged(): boolean {
    return this.phoneDraft.trim() !== this.savedPhone();
  }

  private get companyNameChanged(): boolean {
    return this.companyNameDraft.trim() !== this.savedCompanyName();
  }

  private get facilityChanged(): boolean {
    return this.facilityEditable && this.facilityDraft !== this.facilityCfgId;
  }

  // Facility can only be changed for European (bu_id 7) contacts.
  get facilityEditable(): boolean {
    return Number(this.formData.bu_id) === EUROPE_BU_ID;
  }

  private get currencyChanged(): boolean {
    return this.facilityEditable && this.currencyDraft !== (this.currencies.includes(this.defaultCurrency) ? this.defaultCurrency : null);
  }

  // Read-only facility text for contacts outside bu_id 7.
  get facilityLabel(): string {
    if (this.facilityCfgId === US_CFG_ID) { return 'US'; }
    return this.facilities.find(f => f.cfgId === this.facilityCfgId)?.label ?? '—';
  }

  private savedFirstName(): string {
    return (this.formData.admin_first_name ?? '').toString().trim();
  }

  private savedLastName(): string {
    return (this.formData.admin_last_name ?? '').toString().trim();
  }

  private savedPhone(): string {
    return (this.formData.admin_phone ?? '').toString().trim();
  }

  private savedCompanyName(): string {
    return (this.formData.admin_company_name ?? '').toString().trim();
  }

  // bu_id decides routing, so it is read first: every bu_id 1 contact is US
  // whatever its cfg_id, and a European contact maps to its cfg_id.
  private facilityFromContact(contact: any): number | null {
    const buId = Number(contact?.bu_id);
    const cfgId = Number(contact?.cfg_id);
    if (buId === US_BU_ID) { return US_CFG_ID; }
    if (buId === EUROPE_BU_ID && this.facilities.some(f => f.cfgId === cfgId)) { return cfgId; }
    return null;
  }

  private currencyFromContact(contact: any): string {
    return (contact?.default_currency_code ?? '').toString().trim().toUpperCase();
  }

  getNetsuiteAccount() {
    if (this.formData.profileID == '' || this.formData.profileID == null) { return; }
    this.netsuiteService.getAccountFromProfile(this.formData.profileID).subscribe({
      next: (account) => {
        this.netsuiteAccountId = (account?.netsuite_account_id ?? '').toString().trim();
        this.resetNetsuiteAccountId();
      },
      // A profile with no NetSuite account yet is a normal state, not a failure
      // worth putting in front of the user — the field just starts empty.
      error: (err: any) => console.warn(`netsuite account lookup failed for profile ${this.formData.profileID}`, err)
    })
  }

  get netsuiteAccountIdChanged(): boolean {
    return this.netsuiteAccountIdDraft.trim() !== this.netsuiteAccountId.trim();
  }

  get canSaveNetsuiteAccountId(): boolean {
    const netsuite_account_id = this.netsuiteAccountIdDraft.trim();
    return this.netsuiteAccountIdChanged
      && !this.savingNetsuiteAccountId
      && netsuite_account_id != ''
      && netsuite_account_id.length <= this.netsuiteAccountIdMaxLength
      && this.formData.profileID != '';
  }

  resetNetsuiteAccountId() {
    this.netsuiteAccountIdDraft = this.netsuiteAccountId.trim();
  }

  saveNetsuiteAccountId() {
    const netsuite_account_id = this.netsuiteAccountIdDraft.trim();
    if (this.formData.profileID == '' || this.formData.profileID == null) {
      this.notification.error('No QP profile to associate');
      return;
    }
    if (netsuite_account_id == '') {
      this.notification.error('NetSuite Account ID is required');
      return;
    }

    // ModifiedBy is the acting user, read off the ProfileId claim on the JWT.
    const modifiedBy = (this.token.getValue()?.ProfileId ?? '').toString().trim();
    if (modifiedBy == '') {
      this.notification.error('Could not associate account — no profile on your token');
      return;
    }

    this.savingNetsuiteAccountId = true;
    this.netsuiteService.associateAccount(this.formData.profileID, netsuite_account_id, modifiedBy).subscribe({
      next: (account) => {
        this.netsuiteAccountId = (account?.netsuite_account_id ?? '').toString().trim() || netsuite_account_id;
        this.resetNetsuiteAccountId();
        this.savingNetsuiteAccountId = false;
        this.notification.success('NetSuite account associated');
      },
      error: (err: any) => {
        this.savingNetsuiteAccountId = false;
        console.error('associate netsuite account failed', err);
        this.notification.error('Could not associate NetSuite account');
      }
    })
  }


  // hs_account_name and hs_phone are only returned by get-contact-from-profile,
  // not by the get-profile-from-contact call that loads this page
  getHubspotContactDetails() {
    if (this.formData.profileID == '' || this.formData.profileID == null) { return; }
    this.contactService.getContactFromProfileId(this.formData.profileID).subscribe({
      next: (responseMScontact: any) => {
        this.formData.hs_account_name = responseMScontact?.hs_account_name ?? "";
        this.formData.hs_phone = responseMScontact?.hs_phone ?? "";
      }
    })
  }

  getQuoteList(profileID: string = "") {
    const quoteinfotext = document.getElementById('quoteinfotext') as HTMLElement;
    const orderinfotext = document.getElementById('orderinfotext') as HTMLElement;

    if (profileID != "") { this.formData.profileID = profileID; }

    let params = [{ key: "profile_id", value: this.formData.profileID }]
    this._http.getAdmin('admin/quote/active-quotes', params).subscribe({
      next: (responseITOquotes: any) => {
        responseITOquotes.forEach((val: any, key: any) => {
          // console.debug(`Quote key: ${key} | val: `, val);
          var res = responseITOquotes[key];
          const rawdate = new Date(res.create_date);

          var ecastatus: string = "ECA";
          var ecastatusint: string = "1";
          if (res.is_export_controlled == null || res.is_gov_contract == null || res.is_gov_contract == 3) {
            ecastatus = "Pending Verification";
            ecastatusint = "2";
          } else if (res.is_export_controlled == false && res.is_gov_contract == 2) {
            ecastatus = "Non-ECA";
            ecastatusint = "3";
          }

          let newquote: QuoteData = ({
            quoteID: res.quote_id,
            buid: this.formData.bu_id,
            datecreated: rawdate.toLocaleDateString(),
            status: res.status,
            ecastatus: ecastatus,
            ecastatusint: ecastatusint,
            // 3570 — CM_GetOpportunities resolves this to 'Online' or 'Sales', so it
            // displays as-is. Only absent if the API is ahead of the sproc.
            origin: res.quote_origin?.trim() || 'Unknown',
            amount: res.price ?? null,
          });

          if (["ordered", "Closed Won", "Closed Won - Update in Progress"].includes(res.status)) {
            this.orderrows.push(newquote);
          } else {
            this.quoterows.push(newquote);
          }

        });
        console.log(`getQuoteList complete should fire now`);
      },
      complete: () => {
        // Update data sources for pagination
        this.quoteDataSource.data = this.quoterows;
        this.orderDataSource.data = this.orderrows;

        // Reconnect paginators after data update
        if (this.quotePaginator) {
          this.quoteDataSource.paginator = this.quotePaginator;
        }
        if (this.orderPaginator) {
          this.orderDataSource.paginator = this.orderPaginator;
        }
      },
    })


  }

  postNewProfile(email: string) {
    let first = this.formData.hs_first_name;
    let last = this.formData.hs_last_name;
    let country = this.formData.hs_country;
    console.log(`Posting new profile: ${email} | ${first + ' ' + last} | ${country}`);
    if (country == null || email == null) {
      // valid country code needed to derive business unit
      // valid email required period
      console.log("no hs country, returning"); return;
    }
    const payload = {
      email: email,
      first_name: first,
      last_name: last,
      country: country,
      phone: "",
      locale: "en-US",
    };
    console.log(`payload: `, payload);
    this.contactService.createContact(payload).subscribe({
      next: (profileResponse: any) => {
        console.log('new profile response-->', profileResponse);
        if (profileResponse.qpProfile === undefined || profileResponse.qpProfile === null) {
          console.error(`No profile data in response`);
          return;
        }
        this.formData.profileID = profileResponse.qpProfile.profileId ?? "";
        this.getAdminDetails();
        this.getHubspotContactDetails();
        this.enableQuoteButton();
        this.getQuoteList();
      },
      error: (err: any) => {
        console.error('create contact failed', err);
        this.notification.error('Could not create contact');
      }
    })

  }

  getProfileByEmail() {
    var email = this.formData.hs_email;
    console.log(`Getting profile by email: ${email}`);
    let params = [{ key: "email", value: encodeURIComponent(email) }]
    this._http.getAdmin("admin/contact/find", params).subscribe({
      next: (searchResponse: any) => {
        var doCreate = false;
        if (searchResponse != null) {
          console.log("Email search got a response: ", searchResponse);
          this.formData.admin_full_name = searchResponse.first_name + ' ' + searchResponse.last_name;
          this.formData.admin_email = searchResponse.email;
          if (searchResponse.profile_id == null) {
            console.debug("Profile ID still blank, creating profile");
            doCreate = true;
            this.getQuoteList(searchResponse.profile_id);
            this.enableQuoteButton();
          } else {
            console.log(`Found profile_id: ${searchResponse.profile_id}`);
            this.formData.profileID = searchResponse.profile_id;

            this.getQuoteList(searchResponse.profile_id);
            this.getHubspotContactDetails();
            this.enableQuoteButton();
            return searchResponse.profile_id;
          }
        } else {
          console.debug("Nothing found, creating profile");
          doCreate = true;
        }

        if (doCreate) {
          this.newProfilemodalShow();
        }
      }
    })
  }

  postNewQuote() {
    // this._Activatedroute.data = of({...this._Activatedroute.data, reload: new Date().getTime()});
    // { "for_profile_id": 0 }
    const payload = `{ "for_profile_id": ${this.formData.profileID} }`;
    console.log(`payload: ${payload}`);
    this._http.postAdmin(`admin/quote/`, payload).subscribe((response: any) => {
      console.log('new quote response-->', response);
      const isEU = Number(this.formData.bu_id) === 7;
      const quoteId = response?.quote_unit_id;
      if (isEU && quoteId){
        this.router.navigate(['/sales/quote', quoteId]);
      } else {
        window.location.reload();
      }
    });
  }

  enableQuoteButton() {
    const quotebutton = document.getElementById('btnCreateQuote') as HTMLButtonElement;
    // quotebutton.setAttribute('disabled', 'false');
    quotebutton.disabled = false;
    quotebutton.style.backgroundColor = "";

    console.log(`Enabling quote button`, quotebutton);
  }


  modalopen() { //comes last to hide browser redraw
    console.log('modal opening');
    const mainsection = document.getElementById('main') as HTMLElement;
    mainsection.classList.remove('modalclosed');
    mainsection.classList.add('modalopen');
  }

  sendECAmodalShow(quoteTarget: string) {
    const dialogRef = this.dialog.open(SendVerificationDialogComponent, {
      data: {
        hs_email: this.formData.hs_email,
        admin_email: this.formData.admin_email,
        quoteTarget: quoteTarget
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.sendECA(quoteTarget);
      }
    });
  }

  newProfilemodalShow() {
    const dialogRef = this.dialog.open(ConfirmEmailDialogComponent, {
      data: {
        hs_email: this.formData.hs_email,
        admin_email: this.formData.admin_email
      }
    });

    dialogRef.afterClosed().subscribe(email => {
      if (email) {
        this.postNewProfile(email);
      }
    });
  }

  sendECA(quoteID: any) {
    const ecabutton = document.getElementById(`eca${quoteID}`) as HTMLElement;
    const ecalabel = document.getElementById(`out${quoteID}`) as HTMLElement;
    console.log(`Sending ECA to ${quoteID}`);
    this._http.postAdmin(`admin/quote/eca-email/${quoteID}`).subscribe((response: any) => {
      console.log('send verification response-->', response);
    })
    ecabutton.remove();
  }

  getQuoteDetail(quote_id: any, isECA: any) {
    if (isECA == 2) {
      this.notification.warning("This quote is pending ECA status verification from the customer.")
      return
    } else {
      this.router.navigateByUrl('sales/quote/' + quote_id);
    }
  }

  // Ordered quotes open on the Order page — the quote itself is read-only by
  // then, and the Quote crumb is one click away if needed.
  getOrderDetail(quote_id: any, isECA: any) {
    if (isECA == 2) {
      this.notification.warning("This quote is pending ECA status verification from the customer.")
      return
    }
    this.router.navigateByUrl('sales/quote/order/' + quote_id);
  }



}
